import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
import type { Reservation, SearchSubmissionType } from "../types"
import { getDefaultDateAndTime } from "../helpers"
import { INSTALLATION_TOKEN_LOCALSTORAGE_KEY } from '../constants'

export function getSearchSubmissionLength(searchSubmission: SearchSubmissionType): number {

    const normalisedBeginsAtTime = searchSubmission.startTime === '00:01' ? '00:00' : searchSubmission.startTime
    const normalisedEndsAtTime = searchSubmission.endTime === '00:01' ? '00:00' : searchSubmission.endTime
    
    const startMoment = moment(`${searchSubmission.startDate?.format('YYYY-MM-DD')} ${normalisedBeginsAtTime}`, 'YYYY-MM-DD HH:mm')
    const endMoment = moment(`${searchSubmission.endDate?.format('YYYY-MM-DD')} ${normalisedEndsAtTime}`, 'YYYY-MM-DD HH:mm')

    const searchSubmissionLengthHours = endMoment.diff(startMoment, 'hours')

    return searchSubmissionLengthHours
}

export function sanitizeHtml(unsafeHTML: string, allowedTags: string[] = []) {

    const escapedHTMLAttribute = (str: string) => {
        const result = str.replace(/"/g,'&quot;')
        return result
    }

    function sanitize_r(node: HTMLElement) {
        let html = ''
        for (let i = 0; i < node.childNodes.length; i++) {
            // iterate also text nodes
            const child = node.childNodes[i]

            if (child.nodeType === Node.TEXT_NODE) {
                html += child.textContent
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                if (!(child instanceof HTMLElement)) continue

                const lowerCaseTag = child.tagName.toLowerCase()
                if (allowedTags.includes(lowerCaseTag)) {
                    if (lowerCaseTag === 'br') {
                        html += '<br/>'
                    } else if (lowerCaseTag === 'a') {
                        if (!(child instanceof HTMLAnchorElement)) continue // Make sure tag is correct; also TypeScript autocomplete will work.

                        html += '<a '

                        const urlText = child.href || ''
                        const url = sanitizedUrl(urlText)
                        if (url) {
                            html += ` href="${escapedHTMLAttribute(url)}" `
                        }

                        if (child.target === '_blank') {
                            html += ' target="_blank"'
                        }

                        if (child.rel) {
                            html += ` rel="${escapedHTMLAttribute(child.rel)}" `
                        }

                        html += '>'
                        html += sanitize_r(child)
                        html += '</a>'
                    } else if (lowerCaseTag === 'img') {
                        if(!(child instanceof HTMLImageElement)) continue // Make sure tag is correct; also TypeScript autocomplete will work.

                        html += `<${lowerCaseTag}`

                        const url = child.src
                        html += ` src="${escapedHTMLAttribute(url)}" `

                        html += ` title="${escapedHTMLAttribute(child.title)}" `

                        html += '/>'
                    } else {
                        html += `<${lowerCaseTag}>`
                        html += sanitize_r(child)
                        html += `</${lowerCaseTag}>`
                    }
                }
            }
        }

        return html
    }

    function sanitizedUrl(url: string): string {
        // whitelist what is safe to use
        let isSafe = false
        isSafe ||= url.startsWith('http:')
        isSafe ||= url.startsWith('https:')
        isSafe ||= url.startsWith('mailto:')
        isSafe ||= url.startsWith('//') // protocol is not required
        isSafe ||= url.startsWith('/') // absolute url
        // isSafe ||= url.startsWith('./') // relative url - currently not supported by WYSIWYG
        return isSafe ? url : ''
    }

    try {
        const dom: Document = new DOMParser().parseFromString(unsafeHTML, 'text/html')
        const html = sanitize_r(dom.body)
        return html
    } catch (_) {
        return ''
    }
}

export function getInitialSearch() {
    const {
        defaultStartDate,
        defaultEndDate,
        defaultStartTime,
        defaultEndTime,
    } = getDefaultDateAndTime()

    const searchParams = new URLSearchParams(document.location.search)

    const paramLocation =     null
    const paramBeginsAtDate = searchParams.get('begins_at_date') || defaultStartDate
    const paramBeginsAtTime = searchParams.get('begins_at_time') || defaultStartTime
    const paramEndsAtDate =   searchParams.get('ends_at_date')   || defaultEndDate
    const paramEndsAtTime =   searchParams.get('ends_at_time')   || defaultEndTime

    return {
        paramLocation,
        paramBeginsAtDate,
        paramBeginsAtTime,
        paramEndsAtDate,
        paramEndsAtTime,
    }
}

export const sortReservationsByDate = (reservations: Array<Reservation>) => {
    return (
        reservations.sort((a, b) => {
            const dateA = a.startDate
            const dateB = b.startDate
            if (dateB === null && dateA === null) {
                return 0
            }
            if (dateB === null) {
                return 1
            }
            if (dateA === null) {
                return -1
            }
            if (dateB < dateA) {
                return -1
            }
            if (dateB > dateA) {
                return 1
            }
            return 0
        })
    )
}

export function getOrCreateInstallationToken(): string {
    // biome-ignore lint: casting window as any to avoid extending the interface
    let installationToken: string | null = (window as any).nativeDeviceId || null
    if (!installationToken) {
        installationToken = localStorage.getItem(INSTALLATION_TOKEN_LOCALSTORAGE_KEY);
        if (!installationToken) {
            installationToken = crypto.randomUUID();
            localStorage.setItem(INSTALLATION_TOKEN_LOCALSTORAGE_KEY, installationToken);
        }
    }
    return installationToken
}
